import React, { useMemo } from 'react';
import { useTranslation } from '../../../../../core';
import { css, spacing, StyleSheet } from '../../../../../../styles';
import { CompanyDetailsFooter } from '../CompanyDetailsFooter';
import { CollapsedFieldSection, CompanyFieldGrid, DetailsSection } from '../CompanyDetailsFields';
import { FIELDS, FIELD_TYPES } from '../../utils/constants';

/**
 * Renders editable general company settings and admin tools.
 *
 * @param {object} props
 * @param {Function} props.getFieldValue
 * @param {boolean} props.hasChanges
 * @param {boolean} props.isUpdatingCompany
 * @param {Function} props.onChangeProperty
 * @param {Function} props.onClose
 * @param {Function} props.onSave
 */
export const GeneralTab = ({
    getFieldValue,
    hasChanges,
    isUpdatingCompany,
    onClose,
    onChangeProperty,
    onSave
}) => {
    const { t } = useTranslation();

    const generalCompanyFields = useMemo(() => [
        { fieldName: FIELDS.companyName, label: t('companies.companyName'), isReadOnly: true },
        { fieldName: FIELDS.email, label: t('companies.email'), isReadOnly: true },
        {
            fieldName: FIELDS.region,
            label: t('region'),
            type: FIELD_TYPES.select,
            options: [{ text: 'England', value: 0 }, { text: 'German', value: 1 }],
            onChange: onChangeProperty(FIELDS.region)
        },
        {
            fieldName: FIELDS.appLanguage,
            label: t('companies.appLanguage'),
            type: FIELD_TYPES.select,
            options: [{ text: 'English', value: 5 }, { text: 'German', value: 7 }],
            onChange: onChangeProperty(FIELDS.appLanguage)
        },
        {
            fieldName: FIELDS.isVerified,
            label: t('companies.verified'),
            type: FIELD_TYPES.switch,
            onChange: onChangeProperty(FIELDS.isVerified)
        },
        {
            fieldName: FIELDS.isManuallyVerified,
            label: t('companies.isManuallyVerified'),
            type: FIELD_TYPES.switch,
            onChange: onChangeProperty(FIELDS.isManuallyVerified)
        },
        {
            fieldName: FIELDS.showPopupNotifications,
            label: t('companies.showPopupNotifications'),
            type: FIELD_TYPES.switch,
            onChange: onChangeProperty(FIELDS.showPopupNotifications)
        }
    ], [onChangeProperty, t]);

    const locationFields = useMemo(() => [
        { fieldName: FIELDS.workPhone, label: t('companies.workPhone'), isReadOnly: true },
        { fieldName: FIELDS.country, label: t('companies.country'), isReadOnly: true },
        { fieldName: FIELDS.state, label: t('companies.state'), isReadOnly: true },
        { fieldName: FIELDS.city, label: t('companies.city'), isReadOnly: true },
        { fieldName: FIELDS.address1, label: t('companies.address1'), isReadOnly: true },
        { fieldName: FIELDS.address2, label: t('companies.address2'), isReadOnly: true },
        { fieldName: FIELDS.zipCode, label: t('companies.zipCode'), isReadOnly: true },
        { fieldName: FIELDS.billingCountry, label: t('companies.billingCountry'), isReadOnly: true },
        { fieldName: FIELDS.billingState, label: t('companies.billingState'), isReadOnly: true },
        { fieldName: FIELDS.billingCity, label: t('companies.billingCity'), isReadOnly: true },
        { fieldName: FIELDS.billingAddress1, label: t('companies.billingAddress1'), isReadOnly: true },
        { fieldName: FIELDS.billingAddress2, label: t('companies.billingAddress2'), isReadOnly: true },
        { fieldName: FIELDS.billingZipCode, label: t('companies.billingZipeCode'), isReadOnly: true }
    ], [t]);

    return (
        <div>
            <DetailsSection title={t('companies.details.sections.companyInformation')}>
                <CompanyFieldGrid fields={generalCompanyFields} getFieldValue={getFieldValue} />
                <div className={css(styles.locationDetails)}>
                    <CollapsedFieldSection
                        title={t('companies.locationDetails')}
                        fields={locationFields}
                        itemKey="locationDetails"
                        getFieldValue={getFieldValue}
                    />
                </div>
            </DetailsSection>
            <CompanyDetailsFooter
                hasChanges={hasChanges}
                isUpdatingCompany={isUpdatingCompany}
                onClose={onClose}
                onSave={onSave}
            />
        </div>
    );
};

const styles = StyleSheet.create({
    locationDetails: {
        marginTop: spacing.s4
    }
});
